import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const OsseousSurgery = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Osseous Surgery Fort Worth TX | Periodontal Pocket Reduction | Fort Worth Dental</title>
        <meta name="description" content="Osseous surgery (pocket reduction) in Fort Worth TX. Advanced periodontal treatment to restore gum health. Expert periodontists. Call (817) 985-3337." />
        <meta name="keywords" content="osseous surgery fort worth, pocket reduction fort worth tx, periodontal surgery, gum surgery, bone surgery dental" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/dentist-examining.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Osseous Surgery in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Advanced Treatment for Periodontal Disease</p>
            <p className="text-sm mt-4 opacity-90">Flap Surgery | Pocket Reduction | Gum Disease Treatment</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Osseous Surgery */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/osseous-new-1.jpg" 
                  alt="Osseous Surgery" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is Osseous Surgery?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Osseous surgery is a last-resort effort to strengthen the area around a tooth in order to preserve the tooth. This surgery, also known as flap surgery, is typically performed when the socket around a tooth or several teeth has failed to respond to other treatments. The purpose of the treatment is to create a strong environment around a tooth. It is generally used only when other treatments have been performed and the pocket health has continue to deteriorate.
                </p>
              </div>
            </div>
          </section>

          {/* What Is The Procedure */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is The Procedure For Osseous Surgery?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  In preparation for the procedure, the affected area will receive a local anesthetic in order to numb it. Next, the dentist will make an incision in the gum tissue around the area that needs to be cleaned and treated. The tooth and underlying bone is revealed by gently lifting the gum away. The area surrounding the tooth is then cleaned of any plaque and tartar build up. Once the cleaning is done, the dentist will clean the surface of the bone. Any bacteria that are trapped around the tooth will slowly eat away at the bone and make it uneven. If the area is to heal properly, the bone must have these rough, uneven surfaces smoothed. After the cleaning and smoothing, the dentist will trim the gum tissue to fit to the new underlying structure. He will then stitch the gum tissue snugly back in place.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/osseous-new-2.jpg" 
                  alt="Osseous Surgery Procedure" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Post-Procedure Care */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/osseous-new-1.jpg" 
                  alt="Post-Procedure Care" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Happens After The Procedure?</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Your dentist will provide procedures for you to follow after the procedure. You will likely need pain medication. Your dentist should remove the stitches in 6-10 days after the procedure. Your dentist will want to see you about a month later in order to examine the healing of the area. Be aware that the trimmed gum tissue may cause your teeth to seem longer in the affected area. Those teeth may also be more sensitive to temperatures.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Be sure to exercise good oral hygiene after the treatment in order to avoid future infection.
                </p>
              </div>
            </div>
          </section>

          {/* Benefits and Recovery */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Benefits of Osseous Surgery</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Eliminates deep periodontal pockets</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Removes bacteria and infection</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Prevents tooth loss from advanced gum disease</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Promotes gum reattachment to teeth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Improves long-term oral health</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Creates a healthier environment around teeth</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Osseous Surgery?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced periodontal team provides advanced osseous surgery with compassionate care.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Advanced Techniques</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Save Your Teeth with Osseous Surgery</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about osseous surgery in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Osseous Surgery Consultation in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default OsseousSurgery;